import { day, isSameOrAfter, isSameOrBefore } from '~/astra'
import type { AnyDate } from '~/utils/dateKeys'

import { InboxEntry, useInboxEntries } from '../'

export type InboxEntrySourceType = InboxEntry['source']['attribute_type']
export type InboxEntrySource = InboxEntry['source']

export type CommentsThreadMentionSource = Extract<InboxEntrySource, { attribute_type: 'comments_thread_mention_inbox_entry_source' }>
export type CommentsMessageMentionSource = Extract<InboxEntrySource, { attribute_type: 'comments_message_mention_inbox_entry_source' }>
export type CommentsThreadInboxSource = Extract<
    InboxEntrySource,
    { attribute_type: 'comments_thread_mention_inbox_entry_source' | 'comments_thread_reply_inbox_entry_source' }
>

export const isCommentsThreadMentionSource = (source: InboxEntrySource): source is CommentsThreadMentionSource =>
    source.attribute_type === 'comments_thread_mention_inbox_entry_source'

export const isCommentsMessageMentionSource = (source: InboxEntrySource): source is CommentsMessageMentionSource =>
    source.attribute_type === 'comments_message_mention_inbox_entry_source'

export const isCommentsThreadInboxSource = (source: InboxEntrySource): source is CommentsThreadInboxSource =>
    source.attribute_type === 'comments_thread_mention_inbox_entry_source' || source.attribute_type === 'comments_thread_reply_inbox_entry_source'

export type InboxEntriesFilter = 'all' | 'unread'

const INBOX_ENTRIES_ORDER = [['created_at', 'desc']] as const

export function filterInboxEntriesByDateRange(entries: readonly InboxEntry[], startDate: AnyDate | null, endDate: AnyDate | null): InboxEntry[] {
    const start = day(startDate).startOf('day')
    const end = day(endDate).endOf('day')
    if (!startDate && !endDate) {
        return [...entries]
    }
    return entries.filter(entry => {
        const createdAt = day(entry.created_at)
        if (!startDate) {
            return isSameOrBefore(createdAt, endDate)
        }
        if (!endDate) {
            return isSameOrAfter(createdAt, start)
        }
        return isSameOrAfter(createdAt, start) && isSameOrBefore(createdAt, end)
    })
}

export function useInboxEntriesForFilter(filter: InboxEntriesFilter) {
    return useInboxEntries({
        ...(filter === 'unread' ? { where: { is_read: false } } : {}),
        orderBy: INBOX_ENTRIES_ORDER,
    })
}
